/**
 * 管理画面の枠 (ヘッダー / ユーザーメニュー / サイドバー / 通知 / 個人設定) の文言 — 英語。
 */
import type { TranslationOf } from '../../types';
import type { shellJa } from '../ja/shell';

export const shellEn: TranslationOf<typeof shellJa> = {
  header: {
    mcpUpdate: 'Update integration',
    mcpUpdateTitle:
      'The MCP responding on this device ({version}) is older than the published version ({current}). Open the update steps.',
    toggleSidebar: 'Toggle sidebar',
    tagline: 'Document collection solution',
    switchToLight: 'Switch to light mode',
    switchToDark: 'Switch to dark mode',
    help: 'Help',
    helpTitle: 'Help (user manual)',
    bugReport: 'Report a problem',
    requestStatus: 'Request status',
    requestStatusTitle: 'Status of requests and problem reports',
    userMenu: 'User menu',
    currentOrg: 'Current organization',
    orgNameUnset: '(No organization name)',
    roleTitle: 'Role: {role}',
    preferences: 'Preferences',
    logout: 'Sign out',
  },
  language: {
    switchTo: 'Switch display language to {language}',
    saveFailed:
      'Could not save the display language to your account. It is applied in this browser only.',
  },
  nav: {
    sections: {
      caseClient: 'Cases & Clients',
      documents: 'Documents',
      payments: 'Payments',
      communication: 'Communication',
      settings: 'Settings',
      admin: 'Administration',
      platform: 'Platform',
    },
    items: {
      dashboard: 'Dashboard',
      clients: 'Clients',
      clientGroups: 'Client groups',
      tasks: 'Tasks',
      cases: 'Cases',
      householdBudgets: 'Household budgets',
      calendar: 'Calendar',
      messageWorkspace: 'Messages',
      collections: 'Document collection',
      vault: 'Document vault',
      reports: 'Reports',
      templates: 'Templates',
      paymentImports: 'Import statements',
      paymentImportHistory: 'Import history',
      paymentMatches: 'Payment matching',
      delinquency: 'Overdue payers',
      virtualAccounts: 'Virtual accounts',
      receivingAccounts: 'Receiving accounts',
      chatwork: 'Chatwork',
      line: 'LINE',
      orgSettings: 'Organization settings',
      docMaster: 'Document master',
      creditorMaster: 'Creditor master',
      messageSettings: 'Message settings',
      users: 'Users',
      audit: 'Audit',
      platform: 'Platform management',
      superAdmins: 'Super Admins',
      announcements: 'Announcement banners',
    },
    notEnabled: 'Disabled',
  },
  myTasks: {
    title: 'My tasks',
    count: 'My open tasks: {count}',
    countOver: 'My open tasks: {max} or more',
  },
  notifications: {
    bell: 'Notifications',
    title: 'Notifications',
    markAllRead: 'Mark all as read',
    markingAllRead: 'Marking as read...',
    moreActions: 'More actions',
    exportHistory: 'Export activity history (CSV)',
    exporting: 'Exporting...',
    rangeTabs: 'Notification range',
    tabAll: 'All',
    tabUnread: 'Unread',
    filterByType: 'Filter by notification type',
    allTypes: 'All types',
    markedRead: 'Marked {count} as read',
    emptyForType: 'No notifications of this type',
    emptyUnread: 'No unread notifications',
    empty: 'No notifications',
  },
  announcement: {
    dismiss: 'Close {title}',
  },
  preferences: {
    title: 'Preferences',
    subtitle: 'Personal display and notification settings',
    theme: 'Theme',
    themeDescription: 'Choose the display theme',
    notifications: 'Notifications',
    notificationsDescription:
      'Choose which notifications you receive. Notifications turned OFF are not sent to the bell or by email.',
    notificationItems: {
      upload: 'Upload notifications',
      uploadDescription: 'Notify via the bell and email when a client uploads a document',
      rejection: 'Rejection notifications',
      rejectionDescription: 'Notify via the bell and email when a document is sent back',
      completion: 'Completion notifications',
      completionDescription:
        'Notify via the bell and email when all documents in a collection are received',
      message: 'Message notifications',
      messageDescription:
        'Notify via the bell and email when a message arrives from a client, Chatwork or LINE',
      weeklyDigest: 'Weekly digest',
      weeklyDigestDescription:
        'Receive a summary of your cases by email every Monday (coming soon)',
    },
    inApp: 'In-app notifications',
    inAppDescription:
      'While the admin screen is open, new messages and notifications pop up at the top right. This setting is saved to your account and applies on other PCs and browsers too.',
    popup: 'Pop-up notifications',
    popupDescription: 'Show a pop-up at the top right when something new arrives',
    sound: 'Notification sound',
    soundDescription:
      'Play a short sound when something new arrives (it may not play depending on the browser autoplay settings)',
    testSound: 'Play test sound',
    soundUnavailable: 'This browser cannot play the notification sound',
    language: 'Language',
    languageDescription:
      'Choose the display language of the admin screens. Client and case data (names, document names, messages) is not translated',
    lastLogin: 'Last sign-in',
    lastLoginDescription: 'Shows your most recent sign-in time',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    saved: 'Preferences saved',
  },
  permission: {
    noPermissionTitle: 'You do not have permission to view this page',
    noPermissionBody: 'Your role cannot access this screen. Please contact an administrator.',
    checkFailed: 'Could not check your access permissions.',
    reload: 'Reload',
  },
  errorBoundary: {
    message: 'This screen cannot be shown right now. Please reload.',
    reload: 'Reload',
  },
};
